/**
 * Merchant-side bulk Product import.
 *
 * Owners can upload a CSV of products for any shop they manage. If a
 * referenced ``category_name`` doesn't exist on the shop, the importer
 * auto-creates a top-level category on the fly — zero prep required.
 *
 * Includes the same dry-run toggle as the admin importer.
 */
import { useRef, useState } from "react";
import { Upload, Download, FileSpreadsheet, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

export default function MerchantImports() {
    const inputRef = useRef(null);
    const [busy, setBusy] = useState(false);
    const [result, setResult] = useState(null);
    const [filename, setFilename] = useState("");
    const [dryRun, setDryRun] = useState(false);

    const downloadTemplate = async () => {
        try {
            const { data } = await api.get("/merchant/imports/products/template");
            const blob = new Blob([data.content], { type: "text/csv" });
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = data.filename;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
        } catch (e) { toast.error(e?.response?.data?.detail || "Failed to download template"); }
    };

    const upload = async (file) => {
        if (!file) return;
        setFilename(file.name); setBusy(true); setResult(null);
        try {
            const fd = new FormData();
            fd.append("file", file);
            const { data } = await api.post(
                `/merchant/imports/products${dryRun ? "?dry_run=true" : ""}`,
                fd, { headers: { "Content-Type": "multipart/form-data" } },
            );
            setResult(data);
            toast.success(`${data.dry_run ? "DRY RUN · " : ""}${data.created} created · ${data.updated} updated · ${data.categories_created} new categories · ${data.errors} errors`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Upload failed");
        } finally { setBusy(false); }
    };

    return (
        <div data-testid="merchant-imports-page">
            <h1 className="text-2xl font-bold">Bulk Product Import</h1>
            <p className="text-sm text-muted-foreground mt-1">Upload a CSV of products for any shop you manage. If a category doesn't exist, it will be auto-created.</p>

            <div className="mt-5 bg-card border border-border rounded-2xl p-5">
                <div className="flex gap-2 flex-wrap items-center">
                    <button
                        data-testid="merchant-download-template"
                        onClick={downloadTemplate}
                        className="bg-muted text-foreground border border-border rounded-xl px-4 py-2 text-sm font-bold inline-flex items-center gap-2"
                    ><Download size={14} /> Download template</button>
                    <input
                        ref={inputRef}
                        type="file"
                        accept=".csv,text/csv"
                        onChange={(e) => upload(e.target.files?.[0])}
                        className="hidden"
                        data-testid="merchant-imports-file"
                    />
                    <button
                        data-testid="merchant-imports-pick"
                        onClick={() => inputRef.current?.click()}
                        disabled={busy}
                        className={`rounded-xl px-4 py-2 text-sm font-bold disabled:opacity-50 inline-flex items-center gap-2 ${dryRun ? "bg-amber-500 text-amber-950" : "bg-primary text-primary-foreground"}`}
                    >{busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {dryRun ? "Dry-run upload" : "Upload CSV"}</button>
                    <label className="text-xs font-bold inline-flex items-center gap-2 ml-auto cursor-pointer">
                        <input
                            data-testid="merchant-dry-run-toggle"
                            type="checkbox"
                            checked={dryRun}
                            onChange={(e) => setDryRun(e.target.checked)}
                            className="accent-amber-500 w-4 h-4"
                        />
                        Dry run (no writes)
                    </label>
                    {filename && <span className="text-xs text-muted-foreground inline-flex items-center gap-1 basis-full sm:basis-auto"><FileSpreadsheet size={12} /> {filename}</span>}
                </div>

                {result?.dry_run && (
                    <div className="mt-3 bg-amber-50 border border-amber-200 text-amber-900 rounded-xl px-3 py-2 text-xs font-bold inline-flex items-center gap-2">
                        <AlertCircle size={14} /> Dry run — nothing was written. Uncheck "Dry run" and re-upload to commit.
                    </div>
                )}

                {result && (
                    <div className="mt-4" data-testid="merchant-imports-result">
                        <div className="grid grid-cols-4 gap-2 text-xs">
                            <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3"><p className="text-emerald-700 font-black text-base">{result.created}</p><p className="text-emerald-700/70 uppercase tracking-wider">Created</p></div>
                            <div className="bg-sky-50 border border-sky-100 rounded-xl p-3"><p className="text-sky-700 font-black text-base">{result.updated}</p><p className="text-sky-700/70 uppercase tracking-wider">Updated</p></div>
                            <div className="bg-violet-50 border border-violet-100 rounded-xl p-3"><p className="text-violet-700 font-black text-base">{result.categories_created}</p><p className="text-violet-700/70 uppercase tracking-wider">New cats</p></div>
                            <div className="bg-rose-50 border border-rose-100 rounded-xl p-3"><p className="text-rose-700 font-black text-base">{result.errors}</p><p className="text-rose-700/70 uppercase tracking-wider">Errors</p></div>
                        </div>
                        <div className="mt-3 max-h-72 overflow-y-auto bg-muted/30 border border-border rounded-xl divide-y divide-border">
                            {(result.rows || []).map((r, idx) => (
                                <div key={idx} className="px-3 py-2 text-xs flex items-center gap-2">
                                    {r.ok
                                        ? <CheckCircle2 size={14} className="text-emerald-600" />
                                        : <AlertCircle size={14} className="text-rose-600" />}
                                    <span className="font-bold w-14 shrink-0">Row {r.row}</span>
                                    <span className="flex-1 truncate">{r.ok ? r.action : r.error}</span>
                                    {r.ok && r.id && <code className="text-[10px] opacity-60">{r.id.slice(0, 8)}…</code>}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <p className="text-xs text-muted-foreground mt-3">
                CSV columns: <code className="bg-muted px-1 rounded">restaurant_name</code>, <code className="bg-muted px-1 rounded">category_name</code>, <code className="bg-muted px-1 rounded">name</code>, <code className="bg-muted px-1 rounded">description</code>, <code className="bg-muted px-1 rounded">menu_price</code>, <code className="bg-muted px-1 rounded">food_type</code>, <code className="bg-muted px-1 rounded">image_s3_key</code>, <code className="bg-muted px-1 rounded">stock</code>.
            </p>
        </div>
    );
}
